import { authService, type UserAttributes } from '@/services/api/auth.service'
import {
  COGNITO_SCOPES,
  HOSTED_UI_DOMAIN,
  IMPERSONATION_COOKIE_NAME,
  MARQII_COGNITO_CLIENT_ID,
  SSO_OAUTH_CREDS,
} from '@/utils/constants/oauth'
import { Amplify } from 'aws-amplify'

const DEV_MOCKED_ACCOUNT_ID = 'dev-local-account-id'
const DEV_MOCKED_USER_ID = 'dev-local-user-id'
const DEV_MOCKED_EMAIL = 'dev@marqii.com'
const DEV_MOCKED_USER_ATTRIBUTES = {
  sub: DEV_MOCKED_USER_ID,
  email: DEV_MOCKED_EMAIL,
  name: 'Fake Name',
  given_name: 'Fake',
  family_name: 'Name',
  email_verified: 'true',
  'custom:tenantId': DEV_MOCKED_ACCOUNT_ID,
  'custom:optInMarketing': '1',
} as UserAttributes

const getCookieValue = (name: string) =>
  document.cookie
    .split('; ')
    .filter((row) => row.startsWith(name))
    .map((c) => c.split('=')[1])[0]

const isImpersonating = getCookieValue(IMPERSONATION_COOKIE_NAME)

const customClientId = getCookieValue(MARQII_COGNITO_CLIENT_ID)
const customClientIdOauthUrls =
  SSO_OAUTH_CREDS.has(customClientId) && SSO_OAUTH_CREDS.get(customClientId)

export const configureCognito = () => {
  const userPoolId = 'us-east-1_RvmkA3ZEL'
  const userPoolClientId = isImpersonating
    ? '18ugf0n304151b7a5tv3dt45p4'
    : customClientId || '4af42doicu3r2g2ld0su841m2l'

  Amplify.configure({
    Auth: {
      Cognito: {
        // Amazon Cognito User Pool ID
        userPoolId,

        // Amazon Cognito Web Client ID (26-char alphanumeric string)
        userPoolClientId,

        // Maybe add oauth settings for sso
        loginWith: customClientIdOauthUrls
          ? {
              oauth: {
                domain: HOSTED_UI_DOMAIN,
                scopes: COGNITO_SCOPES,
                redirectSignIn: [customClientIdOauthUrls.signInRedirectUrl],
                redirectSignOut: [customClientIdOauthUrls.signOutUrl],
                responseType: 'code',
              },
            }
          : undefined,
      },
    },
  })

  if (import.meta.env.DEV) {
    authService.signIn = () =>
      Promise.resolve({ isSignedIn: true, nextStep: { signInStep: 'DONE' } })

    authService.confirmSignIn = () =>
      Promise.resolve({ isSignedIn: true, nextStep: { signInStep: 'DONE' } })

    authService.signOut = () => Promise.resolve()

    authService.updatePassword = () =>
      Math.random() < 0.5 ? Promise.reject(new Error('ERROR!!')) : Promise.resolve()

    authService.resetPassword = () =>
      Promise.resolve({
        isPasswordReset: true,
        nextStep: {
          codeDeliveryDetails: {
            attributeName: 'email',
            deliveryMedium: 'EMAIL',
            destination: 'l***@e***',
          },
          resetPasswordStep: 'CONFIRM_RESET_PASSWORD_WITH_CODE',
        },
      })

    authService.confirmResetPassword = () => Promise.resolve()

    authService.fetchAuthSession = () =>
      Promise.resolve({
        tokens: {
          accessToken: {
            payload: {
              'cognito:groups': [DEV_MOCKED_ACCOUNT_ID],
              sub: DEV_MOCKED_USER_ID,
            },
            toString: () => 'jwtAccessToken',
          },
          idToken: {
            payload: {
              'cognito:groups': [DEV_MOCKED_ACCOUNT_ID],
              ...DEV_MOCKED_USER_ATTRIBUTES,
            },
            toString: () => 'jwtIdToken',
          },
        },
        userSub: DEV_MOCKED_USER_ID,
      })

    authService.fetchUserAttributes = () => Promise.resolve({ ...DEV_MOCKED_USER_ATTRIBUTES })
  }
}
